import type { TahseelFeeBreakdown } from '@kalba/shared';
import { Price } from '../../../components/ui/price';

interface Props {
  feeBreakdown: TahseelFeeBreakdown | null | undefined;
  totalFee: string | number | null | undefined;
}

function SectionCard({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-xs text-gray-400">{count} line items</span>
      </div>
      {children}
    </div>
  );
}

function PayerBadge({
  incurredMode,
  incurredEntity,
}: {
  incurredMode: string;
  incurredEntity?: string;
}) {
  const isCustomer = incurredMode === 'OnCustomer';
  return (
    <span
      className={
        isCustomer
          ? 'inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700'
          : 'inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600'
      }
    >
      {isCustomer ? 'Customer' : `Entity${incurredEntity ? ` (${incurredEntity})` : ''}`}
    </span>
  );
}

export function FeeBreakdownTab({ feeBreakdown, totalFee }: Props) {
  if (!feeBreakdown) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-12 text-center text-sm text-gray-400">
        No fee breakdown available for this request.
      </div>
    );
  }

  const { services, extraFees, taxes } = feeBreakdown;

  return (
    <div className="space-y-4">
      <SectionCard title="Service Fees" count={services.length}>
        <div className="divide-y divide-gray-100">
          {services.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-400 italic">No service fee lines.</p>
          )}
          {services.map((s, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Service {s.mainServiceCode}-{s.subServiceCode}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Qty {s.quantity}</p>
              </div>
              <Price value={s.amount} className="text-sm font-semibold text-gray-900" />
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Extra Fees" count={extraFees.length}>
        <div className="divide-y divide-gray-100">
          {extraFees.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-400 italic">No extra fee lines.</p>
          )}
          {extraFees.map((f, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{f.descriptionEn}</p>
                <p className="text-xs text-gray-400 mt-0.5" dir="rtl">
                  {f.descriptionAr}
                </p>
                <div className="mt-1.5">
                  <PayerBadge incurredMode={f.incurredMode} incurredEntity={f.incurredEntity} />
                </div>
              </div>
              <Price value={f.total} className="text-sm font-semibold text-gray-900" />
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Taxes" count={taxes.length}>
        <div className="divide-y divide-gray-100">
          {taxes.length === 0 && (
            <p className="px-5 py-4 text-sm text-gray-400 italic">No tax lines.</p>
          )}
          {taxes.map((t, i) => (
            <div key={i} className="flex items-center justify-between px-5 py-3">
              <p className="text-sm font-medium text-gray-900">{t.descriptionEn}</p>
              <div className="flex items-center gap-6 text-xs text-gray-500">
                <span>
                  Taxable <Price value={t.taxableAmount} className="font-semibold text-gray-800" />
                </span>
                <span>
                  Tax <Price value={t.taxAmount} className="font-semibold text-gray-800" />
                </span>
                <span>
                  Beeah Share{' '}
                  <Price value={t.beeahAmount} className="font-semibold text-gray-800" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-200 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-500">Total (incl. tax)</p>
          <p className="text-xs text-gray-400 mt-0.5">As charged by Tahseel</p>
        </div>
        <div className="text-3xl font-bold text-gray-900">
          <Price value={totalFee} iconClassName="w-[0.7em] h-[0.7em] -mt-1" />
        </div>
      </div>
    </div>
  );
}
